import { useState, useEffect } from 'react';
import { retainersAPI } from '../../utils/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

const toForm = (r) => ({
  client_id: r?.client_id || '',
  client_name: r?.client_name || '',
  project_name: r?.project_name || '',
  segment: r?.segment || '',
  monthly_amount: r?.monthly_amount?.toString() || '',
  invoice_day: r?.invoice_day?.toString() || '',
  start_date: r?.start_date || '',
  end_date: r?.end_date || '',
});

export const EditRetainerDialog = ({ open, onOpenChange, retainer, canEditAll, clients, segments, onSaved }) => {
  const [form, setForm] = useState(toForm(retainer));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(toForm(retainer)); }, [open, retainer]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const clientOptions = clients.some(c => c.id === form.client_id) || !form.client_id
    ? clients : [{ id: form.client_id, name: form.client_name }, ...clients];

  const handleClientChange = (e) => {
    const client = clientOptions.find(c => c.id === e.target.value);
    setForm({ ...form, client_id: e.target.value, client_name: client?.name || '' });
  };

  const buildPayload = () => {
    const payload = {
      project_name: form.project_name,
      segment: form.segment,
      monthly_amount: parseFloat(form.monthly_amount),
      end_date: form.end_date || null,
    };
    if (!canEditAll) return payload;
    return {
      ...payload,
      client_id: form.client_id,
      client_name: form.client_name,
      invoice_day: parseInt(form.invoice_day, 10),
      start_date: form.start_date,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await retainersAPI.update(retainer.id, buildPayload());
      toast.success('Retainer updated successfully');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update retainer');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-retainer-dialog">
        <DialogHeader>
          <DialogTitle>Edit Retainer</DialogTitle>
          <DialogDescription>
            {canEditAll
              ? 'Schedule changes (invoice day, start or end date) update only months not yet invoiced.'
              : 'Update retainer details. Amount changes apply to future months only.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {canEditAll && (
            <div className="space-y-2">
              <Label>Client</Label>
              <select className={selectClass} value={form.client_id} onChange={handleClientChange} required data-testid="edit-client-select">
                {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Project Name</Label>
            <Input value={form.project_name} onChange={set('project_name')} required data-testid="edit-project-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Segment</Label>
              <select className={selectClass} value={form.segment} onChange={set('segment')} data-testid="edit-segment-select">
                {segments.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Monthly Amount (INR)</Label>
              <Input type="number" min="0" step="0.01" value={form.monthly_amount} onChange={set('monthly_amount')} required data-testid="edit-amount-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">Amount changes apply only to future pending months</p>
          <div className={`grid gap-4 ${canEditAll ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {canEditAll && (
              <>
                <div className="space-y-2">
                  <Label>Invoice Day (1-28)</Label>
                  <Input type="number" min="1" max="28" value={form.invoice_day} onChange={set('invoice_day')} required data-testid="edit-day-input" />
                </div>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={set('start_date')} required data-testid="edit-start-input" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={set('end_date')} data-testid="edit-end-input" />
              <p className="text-xs text-muted-foreground">Leave blank for ongoing</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-retainer-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="edit-retainer-submit">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
